import { useSession } from "@how/app-kit";
import { Stack } from "@mantine/core";
import {
  IconActivity,
  IconBug,
  IconBuildingChurch,
  IconCircleCheck,
  IconLifebuoy,
  IconWorldWww,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { DISPLAY_LOCALE } from "@how/ui";
import {
  HomeDashboard,
  type HomeDesign,
  countInto,
  greetingFor,
  lastDays,
  lastMonths,
  runningTotal,
  type DashboardData,
} from "@how/ui/dashboard";
import { supabase } from "../../lib/supabase";
import { usePlatformRole } from "../auth/platform-role";
import { useWebhookFailures } from "../webhooks/webhook-queries";
import { usePlatformActivity, usePlatformStats } from "./queries";
import { TableGrowthCard } from "./TableGrowthCard";

/** Every church's status and when it was created: a few hundred rows at most, read whole (platform staff only). */
function useTenantTimeline() {
  return useQuery({
    queryKey: ["overview-tenant-timeline"],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase.from("tenants").select("status, created_at");
      if (error) throw error;
      return data;
    },
  });
}

/** When each browser error of the last fourteen days happened (ADR 0038). */
function useErrorTimes(since: string) {
  return useQuery({
    queryKey: ["overview-error-times", since],
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("error_events")
        .select("occurred_at")
        .gte("occurred_at", since)
        .limit(10000);
      if (error) throw error;
      return data.map((e) => e.occurred_at);
    },
  });
}

const STATUS_WORDS: Record<string, string> = {
  provisioning: "Provisioning",
  active: "Active",
  suspended: "Suspended",
  offboarding: "Offboarding",
  deleted: "Deleted",
};

/**
 * The control plane's overview in the Horizon design: the platform's own figures, read with the caller's platform role.
 * A super admin also gets the table growth card under it.
 */
export function OverviewPage({ design = "horizon" }: { readonly design?: HomeDesign } = {}) {
  const session = useSession();
  const role = usePlatformRole();
  const stats = usePlatformStats();
  const activity = usePlatformActivity();
  const clock = useMemo(() => {
    const now = new Date();
    const days = lastDays(14, now);
    return { now, days, months: lastMonths(6, now), since: (days[0]?.start ?? now).toISOString() };
  }, []);
  const tenants = useTenantTimeline();
  const errors = useErrorTimes(clock.since);
  const superAdmin = role.data === "super_admin";
  const failures = useWebhookFailures();

  const email = session.status === "signed_in" ? (session.session.user.email ?? "") : "";
  const perDay = errors.data === undefined ? null : countInto(clock.days, errors.data);
  const lastDay = perDay === null ? null : (perDay[perDay.length - 1]?.value ?? 0);
  const added =
    tenants.data === undefined
      ? null
      : countInto(
          clock.months,
          tenants.data.map((t) => t.created_at),
        );
  const byStatus = new Map<string, number>();
  for (const t of tenants.data ?? []) byStatus.set(t.status, (byStatus.get(t.status) ?? 0) + 1);

  const data: DashboardData = {
    surface: "Control plane",
    greeting: greetingFor(clock.now, email.split("@")[0]),
    subtitle:
      "Platform health at a glance: the churches on HoW, who DCX support is helping right now, and what broke lately.",
    metrics: [
      {
        key: "tenants",
        label: "Churches",
        value: stats.data?.tenants ?? null,
        loading: stats.isPending,
        hint: "Every tenant on the platform",
        icon: IconBuildingChurch,
        tone: "iris",
        ...(added === null || stats.data === undefined
          ? {}
          : { series: runningTotal(added, stats.data.tenants) }),
        to: "/tenants",
      },
      {
        key: "active",
        label: "Active",
        value: stats.data?.activeTenants ?? null,
        loading: stats.isPending,
        hint: "Open and in use",
        icon: IconCircleCheck,
        tone: "mint",
      },
      {
        key: "support",
        label: "Support sessions",
        value: stats.data?.activeSessions ?? null,
        loading: stats.isPending,
        hint: "Open right now",
        icon: IconLifebuoy,
        tone: "amber",
        to: "/support",
      },
      {
        key: "errors",
        label: "Errors today",
        value: errors.isError ? null : lastDay,
        loading: errors.isPending,
        hint: "Reported by browsers since midnight",
        icon: IconBug,
        tone: "sky",
        ...(perDay === null ? {} : { series: perDay.map((p) => p.value) }),
        to: "/errors",
      },
    ],
    trend: errors.isError
      ? null
      : {
          title: "Errors reported",
          caption: "Browser errors each day, the last two weeks",
          points: perDay ?? [],
          loading: errors.isPending,
          unit: "Errors each day",
        },
    breakdown: tenants.isError
      ? null
      : {
          title: "Churches by status",
          caption: "Where each tenant is in its life",
          parts: [...byStatus.entries()]
            .map(([status, value]) => ({ label: STATUS_WORDS[status] ?? status, value }))
            .sort((a, b) => b.value - a.value),
          loading: tenants.isPending,
          totalLabel: "churches",
        },
    list: {
      title: "Recent activity",
      caption: "The platform audit trail, newest first",
      loading: activity.isPending,
      empty: activity.isError
        ? "Your platform role cannot read the audit trail."
        : "Nothing has happened yet.",
      items: (activity.data ?? []).slice(0, 6).map((e) => ({
        id: String(e.id),
        title: e.action,
        meta: `${e.actor_type} on ${e.target_table ?? "platform"}`,
        when: new Date(e.occurred_at).toLocaleString(DISPLAY_LOCALE, {
          day: "numeric",
          month: "short",
          hour: "numeric",
          minute: "2-digit",
        }),
        ...(e.actor_type === "platform_support" ? { badge: "Support" } : {}),
      })),
    },
    facts: {
      title: "You and the platform",
      rows: [
        { label: "Signed in as", value: email },
        { label: "Platform role", value: role.data?.replace("_", " ") ?? "—" },
        ...(superAdmin
          ? [
              {
                label: "Webhooks failed, last 24 hours",
                value: failures.isError ? "Not available" : String(failures.data ?? "…"),
              },
            ]
          : []),
        { label: "Host", value: window.location.hostname },
      ],
    },
    actions: [
      { label: "Churches", to: "/tenants", icon: IconBuildingChurch },
      { label: "Support", to: "/support", icon: IconLifebuoy },
      { label: "Errors", to: "/errors", icon: IconActivity },
      { label: "Domains", to: "/domains", icon: IconWorldWww },
    ],
  };

  return (
    <Stack gap={22}>
      <HomeDashboard data={data} design={design} />
      {superAdmin ? <TableGrowthCard /> : null}
    </Stack>
  );
}
